import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import { formatSlotTime } from "../lib/timeFormat";
import { PublicShell } from "./PublicLandingPage";
import { BookingHeader, TimeslotPicker } from "../components/BookingChrome";
import { getTemplate, mergeBranding } from "../lib/templates";
import { trackFunnel, getAttributionPayload } from "../lib/track";
import { Plus, Minus, ArrowLeft } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "../lib/stripe";

const PANEL_HEADINGS = {
  default: "Your Visit", carnival: "Your Booking", haunt: "Your Visit",
  asylum: "Admission Record", crypt: "Your Visit",
};

function fmtDate(ds, tplId) {
  if (!ds) return "—";
  const [y, m, d] = ds.split("-").map(Number);
  if (tplId === "asylum") return `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`;
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

// Discounted tickets are capped: the first `discount_remaining` units use the tier price,
// any extra units roll to the base price (matches backend split pricing).
function ttSplit(t, qty) {
  const eff = t.effective_price_pence ?? t.base_price_pence;
  const base = t.base_price_pence;
  const rem = t.discount_remaining;
  if (eff < base && rem != null && qty > rem) {
    return { discountQty: rem, fullQty: qty - rem, eff, base, capped: true };
  }
  return { discountQty: qty, fullQty: 0, eff, base, capped: false };
}
function ttLineTotal(t, qty) {
  const s = ttSplit(t, qty);
  return s.eff * s.discountQty + s.base * s.fullQty;
}


function detectCountry() {
  try {
    const l = navigator.language || "en-GB";
    const region = (l.split("-")[1] || "").toUpperCase();
    if (/^[A-Z]{2}$/.test(region)) return region; // valid ISO 3166-1 alpha-2 only
  } catch (_) { /* noop */ }
  return "GB";
}

export default function PublicBookingFlow() {
  const { slug, dateId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [items, setItems] = useState({});
  const [addonItems, setAddonItems] = useState({});
  const [stage, setStage] = useState(null);
  const [form, setForm] = useState({ first: "", last: "", email: "", phone: "", postcode: "", terms: false });
  const [showTerms, setShowTerms] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pi, setPi] = useState(null); // { clientSecret, publishableKey, orderId } once a PaymentIntent exists
  const [attendeeNames, setAttendeeNames] = useState([]);
  const country = useMemo(detectCountry, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/attraction/${slug}/date/${dateId}`);
        setData(r.data);
        trackFunnel("date", { attraction_id: r.data.attraction?.id, event_id: r.data.event?.id, window_days: r.data.attraction?.attribution_window_days, date_id: dateId });
        setStage((prev) => prev || (r.data.event.is_time_slotted ? "slot" : "tickets"));
      } catch { setErr("Date not available"); }
    })();
  }, [slug, dateId]);

  const totalPence = useMemo(() => {
    if (!data) return 0;
    const sp = (slotId && data.event.is_time_slotted) ? (data.time_slots.find((x) => x.id === slotId)?.ticket_pricing || null) : null;
    let total = 0;
    Object.entries(items).forEach(([ttid, qty]) => {
      const tt = data.ticket_types.find((t) => t.id === ttid);
      if (tt) total += ttLineTotal(sp && sp[ttid] ? { ...tt, ...sp[ttid] } : tt, Number(qty));
    });
    Object.entries(addonItems).forEach(([aid, qty]) => {
      const ad = data.addons.find((a) => a.id === aid);
      if (ad) total += ad.price_pence * Number(qty);
    });
    return total;
  }, [items, addonItems, data, slotId]);

  const totalQty = Object.values(items).reduce((s, q) => s + Number(q), 0);

  // Log "reached checkout" the moment they land on the checkout page (captures abandoners, not just payers).
  useEffect(() => {
    if (stage === "checkout" && data) {
      trackFunnel("checkout", {
        attraction_id: data.attraction?.id, event_id: data.event?.id,
        window_days: data.attraction?.attribution_window_days,
        tickets: totalQty, value_pence: totalPence, date_id: dateId, slot_id: slotId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (err) return <PublicShell><div className="text-center py-32 text-[#fca5a5]">{err}</div></PublicShell>;
  if (!data || !stage) return <PublicShell><div className="text-center py-32 text-[#8a8a8a]">Loading…</div></PublicShell>;

  const a = data.attraction;
  const b = mergeBranding(a.branding);
  const tplId = getTemplate(b.template_id || "default").id;
  const slotted = !!data.event.is_time_slotted;
  const slot = data.time_slots.find((s) => s.id === slotId);
  const slotPricing = slotted && slot ? (slot.ticket_pricing || {}) : null;
  const priceFor = (t) => (slotPricing && slotPricing[t.id]) ? { ...t, ...slotPricing[t.id] } : t;
  const visibleTypes = (data.ticket_types || []).filter((t) => !(slotPricing && slotPricing[t.id] && slotPricing[t.id].disabled));
  const hasAddons = data.addons.length > 0;
  const paymentsEnabled = !!data.payments_enabled;
  const connected = paymentsEnabled && !!a.stripe_onboarding_complete;
  const collectNames = !!a.collect_attendee_names;
  const nameSlots = [];
  Object.entries(items).filter(([, q]) => Number(q) > 0).forEach(([ttid, q]) => {
    const tt = data.ticket_types.find((t) => t.id === ttid);
    for (let i = 0; i < Number(q); i++) nameSlots.push({ ttid, label: `${tt ? tt.name : "Ticket"} ${i + 1}` });
  });
  const attendeesOk = !collectNames || nameSlots.every((_, i) => i === 0 || (attendeeNames[i] || "").trim());

  const adjust = (id, delta, max = null) => {
    setItems((prev) => {
      const cur = Number(prev[id] || 0) + delta;
      const v = Math.max(0, max ? Math.min(max, cur) : cur);
      const out = { ...prev, [id]: v };
      if (v === 0) delete out[id];
      return out;
    });
  };
  const adjustAddon = (id, delta, max = null) => {
    setAddonItems((prev) => {
      const cur = Number(prev[id] || 0) + delta;
      const v = Math.max(0, max ? Math.min(max, cur) : cur);
      const out = { ...prev, [id]: v };
      if (v === 0) delete out[id];
      return out;
    });
  };

  // Back navigation — new order (slotted): slot → tickets → [attendees] → [addons] → checkout.
  const back = (() => {
    if (stage === "slot") return { label: "Back to dates", go: () => nav(`/${slug}/${data.event.slug}`) };
    if (stage === "tickets") return slotted
      ? { label: "Back to time slots", go: () => setStage("slot") }
      : { label: "Back to dates", go: () => nav(`/${slug}/${data.event.slug}`) };
    if (stage === "addons") return { label: "Back to tickets", go: () => setStage("tickets") };
    if (stage === "checkout") return { label: hasAddons ? "Back to add-ons" : "Back to tickets", go: () => setStage(hasAddons ? "addons" : "tickets") };
    return { label: "Back", go: () => nav(`/${slug}/${data.event.slug}`) };
  })();

  const buildPayload = () => ({
    slug, event_date_id: dateId, time_slot_id: slotId,
    customer_first_name: form.first, customer_last_name: form.last,
    customer_name: `${form.first} ${form.last}`.trim(),
    customer_email: form.email, customer_phone: form.phone || "", postcode: form.postcode || "",
    items: Object.entries(items).filter(([_, q]) => Number(q) > 0).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity: Number(quantity) })),
    addons: Object.entries(addonItems).filter(([_, q]) => Number(q) > 0).map(([addon_id, quantity]) => ({ addon_id, quantity: Number(quantity) })),
    attendee_names: collectNames ? nameSlots.map((_, i) => i === 0 ? `${form.first} ${form.last}`.trim() : (attendeeNames[i] || "").trim()) : [],
    accepted_terms: a.terms ? form.terms : true,
    ...getAttributionPayload(a.attribution_window_days),
  });

  // Mock checkout — used when Stripe is not configured on the platform (demo flow).
  const submit = async () => {
    setErr(""); setSubmitting(true);
    try {
      const r = await api.post("/checkout", { ...buildPayload(), payment_method: "mock" });
      nav(`/${slug}/confirmation/${r.data.order_id}`);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  // Real card payment — create a PaymentIntent, then mount the Stripe Payment Element.
  const startPayment = async () => {
    setErr(""); setSubmitting(true);
    try {
      const r = await api.post("/checkout/create-payment-intent", buildPayload());
      setPi({ clientSecret: r.data.client_secret, publishableKey: r.data.publishable_key, stripeAccount: r.data.stripe_account, orderId: r.data.order_id });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  // After tickets (non-slotted) or after slot (slotted): go to add-ons / checkout. Attendee names are collected on the checkout page.
  const afterSelection = () => {
    trackFunnel("tickets", {
      attraction_id: data?.attraction?.id, event_id: data?.event?.id,
      window_days: data?.attraction?.attribution_window_days,
      tickets: totalQty, value_pence: totalPence, date_id: dateId, slot_id: slotId,
    });
    setStage(hasAddons ? "addons" : "checkout");
  };

  let cta = null;
  if (stage === "tickets") cta = { label: "Continue", disabled: totalQty === 0, onClick: afterSelection };
  else if (stage === "addons") cta = { label: "Continue to checkout", disabled: false, onClick: () => setStage("checkout") };

  const termsOk = a.terms ? form.terms : true;
  const detailsOk = form.first.trim() && form.last.trim() && form.email.trim() && form.phone.trim() && form.postcode.trim();

  return (
    <PublicShell attraction={a} hideLogo>
      <BookingHeader attraction={a} branding={b} />

      <div className="flow-back" data-testid="flow-back">
        <button onClick={back.go} className="inline-flex items-center gap-1.5" data-testid="back-button">
          <ArrowLeft size={15} /> {back.label}
        </button>
      </div>

      <div className="booking-body" data-testid="booking-body">
        <div data-testid="booking-flow-main">
          {stage === "slot" && slotted && (
            <div data-testid="slot-picker">
              <TimeslotPicker slots={data.time_slots} branding={b} attraction={a} selectedId={slotId} onSelect={(s) => { setSlotId(s.id); trackFunnel("slot", { attraction_id: a.id, event_id: data.event?.id, window_days: a.attribution_window_days, date_id: dateId, slot_id: s.id }); setStage("tickets"); }} items={items} ticketTypes={data.ticket_types} />
            </div>
          )}

          {stage === "tickets" && (
            <div data-testid="tickets-picker" className="flow-box">
              <h2 className="pc-heading mb-3">{b.ticket_container_heading || "Choose your tickets"}</h2>
              <div className="space-y-2">
                {visibleTypes.map((t) => {
                  const p = priceFor(t);
                  const qty = Number(items[t.id] || 0);
                  const eff = p.effective_price_pence ?? p.base_price_pence;
                  const discounted = eff < p.base_price_pence;
                  const rem = p.remaining;                          // per-slot remaining (null = unlimited)
                  const soldOut = rem != null && rem <= 0;
                  const maxQ = Math.min(rem != null ? rem : Infinity, t.max_per_order || Infinity);
                  const split = ttSplit(p, qty);
                  const remAtZero = discounted && p.discount_remaining != null ? p.discount_remaining : null;
                  return (
                    <div key={t.id} className={`ticket-item ${soldOut ? "opacity-60" : ""}`} data-testid={`tt-${t.id}`}>
                      <div>
                        <div className="ticket-name">{t.name}</div>
                        {t.description && <div className="ticket-desc" data-testid={`tt-desc-${t.id}`}>{t.description}</div>}
                        <div className="text-sm mt-0.5">
                          {discounted && <span className="line-through text-[#7a7a7a] mr-2">{poundsFromPence(p.base_price_pence)}</span>}
                          <span className="ticket-price">{poundsFromPence(eff)}</span>
                        </div>
                        {soldOut && <div className="text-[12px] text-[#fca5a5] mt-1" data-testid={`tt-soldout-${t.id}`}>Sold out at this time — try another time slot</div>}
                        {!soldOut && rem != null && rem < 10 && (
                          <div className="text-[12px] text-[#d4a64a] mt-1" data-testid={`tt-left-${t.id}`}>Only {rem} left at this time{qty >= maxQ ? " — pick another time slot for more" : ""}</div>
                        )}
                        {discounted && remAtZero != null && remAtZero > 0 && qty <= remAtZero && p.show_remaining_count && (
                          <div className="text-[12px] text-[#d4a64a] mt-1" data-testid={`tt-discount-left-${t.id}`}>
                            Only {remAtZero} ticket{remAtZero === 1 ? "" : "s"} left at this price
                          </div>
                        )}
                        {discounted && p.show_remaining_days && p.discount_days_remaining != null && (
                          <div className="text-[12px] text-[#d4a64a] mt-1" data-testid={`tt-discount-days-${t.id}`}>
                            {p.discount_days_remaining <= 0 ? "Discounted price ends today" : `Discounted price ends in ${p.discount_days_remaining} day${p.discount_days_remaining === 1 ? "" : "s"}`}
                          </div>
                        )}
                        {split.capped && (
                          <div className="text-[12px] text-[#d4a64a] mt-1 leading-snug" data-testid={`tt-discount-warning-${t.id}`}>
                            {split.discountQty > 0
                              ? <>The discounted price has reached its maximum. {split.discountQty} ticket{split.discountQty === 1 ? "" : "s"} at {poundsFromPence(split.eff)}, the remaining {split.fullQty} at the full price of {poundsFromPence(split.base)}.</>
                              : <>The discounted price has sold out. These tickets are at the full price of {poundsFromPence(split.base)}.</>}
                          </div>
                        )}
                      </div>
                      <Counter value={qty} onMinus={() => adjust(t.id, -1)} onPlus={() => adjust(t.id, 1, maxQ === Infinity ? null : maxQ)} disabled={soldOut} atMax={qty >= maxQ} testid={`ticket-counter-${t.id}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {stage === "addons" && (
            <div data-testid="addons-picker" className="flow-box">
              <h2 className="pc-heading mb-3">Would you like to add anything?</h2>
              <div className="space-y-2">
                {data.addons.map((ad) => (
                  <div key={ad.id} className="ticket-item">
                    <div><div className="ticket-name">{ad.name}</div>
                      <div className="text-sm mt-0.5 ticket-price">{poundsFromPence(ad.price_pence)}</div></div>
                    <Counter value={Number(addonItems[ad.id] || 0)} onMinus={() => adjustAddon(ad.id, -1)}
                             onPlus={() => adjustAddon(ad.id, 1, ad.max_per_order ?? ad.max_quantity)} testid={`addon-counter-${ad.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {stage === "checkout" && (
            <div data-testid="checkout-form" className="flow-box">
              <h2 className="pc-heading mb-3">Your details</h2>
              <div className="grid md:grid-cols-2 gap-3">
                <input className="st-input" placeholder="First name" aria-label="First name" required value={form.first} onChange={(e) => setForm({ ...form, first: e.target.value })} disabled={!!pi} data-testid="ck-first" />
                <input className="st-input" placeholder="Last name" aria-label="Last name" required value={form.last} onChange={(e) => setForm({ ...form, last: e.target.value })} disabled={!!pi} data-testid="ck-last" />
                <input className="st-input" type="email" placeholder="Email" aria-label="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!pi} data-testid="ck-email" />
                <div className="st-input phone-field" data-testid="ck-phone">
                  <PhoneInput international defaultCountry={country} value={form.phone} onChange={(v) => setForm({ ...form, phone: v || "" })} placeholder="Phone" disabled={!!pi} />
                </div>
                <input className="st-input" placeholder="Postcode" aria-label="Postcode" required value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} disabled={!!pi} data-testid="ck-postcode" />
              </div>

              {collectNames && nameSlots.length > 0 && (
                <div className="mt-4" data-testid="attendee-fields">
                  <h3 className="text-sm font-semibold mb-1">{b.attendee_step_heading || "Who's coming?"}</h3>
                  <p className="text-xs text-[#9a9a9a] mb-3">{b.attendee_step_subheading || "Enter a name for each ticket so everyone gets their own."}</p>
                  <div className="space-y-2">
                    {nameSlots.map((s, i) => (
                      <div key={i} className="grid md:grid-cols-[150px_1fr] md:items-center gap-1.5 md:gap-2" data-testid={`attendee-row-${i}`}>
                        <div className="text-xs text-[#bbb]">{s.label}{i === 0 && <span className="ml-1 text-[10px] uppercase tracking-wider text-[var(--accent-color,#dc2626)]">Lead</span>}</div>
                        <input className="st-input" placeholder="Full name"
                               value={i === 0 ? `${form.first} ${form.last}`.trim() : (attendeeNames[i] || "")}
                               onChange={(e) => { if (i !== 0) setAttendeeNames((p) => { const n = [...p]; n[i] = e.target.value; return n; }); }}
                               disabled={!!pi || i === 0} data-testid={`attendee-name-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {a.terms && (
                <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.checked })} disabled={!!pi} data-testid="ck-terms" />
                  <span>I accept the <button type="button" className="underline text-[var(--accent-color,#dc2626)]" onClick={() => setShowTerms(true)} data-testid="open-terms">terms &amp; conditions</button></span>
                </label>
              )}

              {err && <div className="text-sm text-[#fca5a5] mt-3" data-testid="checkout-error">{err}</div>}

              {paymentsEnabled && !connected ? (
                <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-4 text-sm text-[#e8c987] mt-5" data-testid="booking-unavailable">
                  Online booking is temporarily unavailable for this attraction. Please check back soon.
                </div>
              ) : pi ? (
                <div className="mt-5">
                  <Elements stripe={getStripe(pi.publishableKey, pi.stripeAccount)} options={{ clientSecret: pi.clientSecret, appearance: stripeAppearance(b) }}>
                    <PaymentInner
                      amount={poundsFromPence(totalPence)}
                      returnUrl={`${window.location.origin}/${slug}/confirmation/${pi.orderId}`}
                      onError={(m) => setErr(m)}
                    />
                  </Elements>
                  <button className="text-xs text-[#8a8a8a] mt-3 underline" onClick={() => setPi(null)} data-testid="edit-details">← Edit details</button>
                </div>
              ) : connected ? (
                <button className="st-btn mt-5 w-full justify-center" disabled={submitting || !termsOk || !detailsOk || !attendeesOk || totalQty === 0}
                        onClick={startPayment} data-testid="continue-to-payment">
                  {submitting ? "Processing…" : "Continue to payment"}
                </button>
              ) : (
                <>
                  <button className="st-btn mt-5 w-full justify-center" disabled={submitting || !termsOk || !detailsOk || !attendeesOk || totalQty === 0}
                          onClick={submit} data-testid="pay-now">
                    {submitting ? "Processing…" : `Pay ${poundsFromPence(totalPence)}`}
                  </button>
                  <p className="text-[10px] text-[#7a7a7a] mt-2">Demo payment — no card is charged. Connect Stripe to take real payments.</p>
                </>
              )}
            </div>
          )}
        </div>

        {cta && (
          <div className="flow-footer" data-testid="flow-footer">
            <div className="flow-footer-summary">
              <span data-testid="footer-date">{fmtDate(data.date.date, tplId)}</span>
              {slotted && slot && <span data-testid="footer-time"> · {formatSlotTime(slot.start_time, slot.end_time, a)}</span>}
              {totalQty > 0 && <span data-testid="footer-qty"> · {totalQty} ticket{totalQty === 1 ? "" : "s"}</span>}
              {totalPence > 0 && <span className="flow-footer-total" data-testid="footer-total"> · {poundsFromPence(totalPence)}</span>}
            </div>
            <button className="st-btn w-full justify-center disabled:opacity-45 disabled:cursor-not-allowed" disabled={cta.disabled} onClick={cta.onClick} data-testid="flow-continue">
              {cta.label}
            </button>
          </div>
        )}
      </div>

      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="terms-modal" onClick={() => setShowTerms(false)}>
          <div className="bg-[#0e0e0e] border border-[#222] rounded-[10px] w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#1a1a1a] text-sm font-semibold">Terms &amp; Conditions</div>
            <div className="flex-1 overflow-y-auto p-4 text-xs text-[#bbb] whitespace-pre-wrap" data-testid="terms-modal-body">{a.terms}</div>
            <div className="p-3 border-t border-[#1a1a1a] flex justify-end gap-2">
              <button className="st-btn st-btn-ghost text-xs" onClick={() => setShowTerms(false)} data-testid="terms-close">Close</button>
              <button className="st-btn text-xs" onClick={() => { setForm((f) => ({ ...f, terms: true })); setShowTerms(false); }} data-testid="terms-accept">Confirm &amp; Accept</button>
            </div>
          </div>
        </div>
      )}
    </PublicShell>
  );
}

function Counter({ value, onMinus, onPlus, disabled, atMax, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <button className="w-8 h-8 rounded-full border border-[#2b2b2b] flex items-center justify-center hover:border-[#dc2626] disabled:opacity-40" onClick={onMinus} disabled={value <= 0} aria-label="minus" data-testid={testid ? `${testid}-minus` : undefined}><Minus size={14} /></button>
      <span className="w-6 text-center font-bold">{value}</span>
      <button className="w-8 h-8 rounded-full border border-[#2b2b2b] flex items-center justify-center hover:border-[#dc2626] disabled:opacity-40 disabled:hover:border-[#2b2b2b]" onClick={onPlus} disabled={disabled || atMax} aria-label="plus" data-testid={testid ? `${testid}-plus` : undefined}><Plus size={14} /></button>
    </div>
  );
}

// Stripe Payment Element appearance, themed from the attraction's branding.
function stripeAppearance(b) {
  return {
    theme: "night",
    variables: {
      colorPrimary: b.btn_bg || b.accent_color || "#dc2626",
      colorBackground: b.panel_bg || "#0e0e0e",
      colorText: b.text_color || "#ffffff",
      borderRadius: `${b.btn_border_radius != null ? b.btn_border_radius : 6}px`,
    },
  };
}

function PaymentInner({ amount, returnUrl, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    const { error } = await stripe.confirmPayment({ elements, confirmParams: { return_url: returnUrl } });
    // On success Stripe redirects to return_url; only errors reach here.
    if (error) { onError(error.message || "Payment could not be completed."); setBusy(false); }
  };
  return (
    <div data-testid="stripe-payment">
      <PaymentElement options={{ disableLink: true }} />
      <button className="st-btn mt-5 w-full justify-center" disabled={!stripe || busy} onClick={pay} data-testid="stripe-pay-now">
        {busy ? "Processing…" : `Pay ${amount}`}
      </button>
    </div>
  );
}
